import { useCallback, RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import useUiuxStore from "src/store/useUiuxStore";
import { focusToCell } from "src/utils/grid/GridHandlerContent";
import { SYS4100_R01Dto } from "../Dto_SYS4100_R01";

interface UseSYS4100_R01ModalProps {
	SYS4100_R01List: SYS4100_R01Dto[];
	updateSYS4100_R01List: (index: number, data: SYS4100_R01Dto) => void;
	originalSYS4100_R01List: SYS4100_R01Dto[];
	gridRef: RefObject<AgGridReact<SYS4100_R01Dto> | null>;
}

export function useSYS4100_R01Modal({
	SYS4100_R01List,
	updateSYS4100_R01List,
	originalSYS4100_R01List,
	gridRef,
}: UseSYS4100_R01ModalProps) {
	const { setCodeSearchModalProps } = useUiuxStore();

	const handlePopupClose = useCallback(() => {
		if (gridRef.current?.api) {
			gridRef.current.api.stopEditing();
		}
	}, [gridRef]);

	const settingCodeSearchModalProps = useCallback(
		(
			category: string,
			search: string,
			isInfo: boolean,
			handleFocusId: string,
			originalData: SYS4100_R01Dto,
			valueIndex: number,
		) => {
			const handleClear = () => {
				const updateData: Partial<SYS4100_R01Dto> = {
					...originalData,
					isModified: originalData.inserted ? false : true,
				};

				if (handleFocusId === "upOrgCd") {
					updateData.upOrgCd = "";
					updateData.upOrgNm = "";
				} else if (handleFocusId === "orgAdmin") {
					updateData.orgAdmin = "";
					updateData.orgAdminNm = "";
				} else if (handleFocusId === "mergeOrgCd") {
					updateData.mergeOrgCd = "";
					updateData.mergeOrgNm = "";
				}

				updateSYS4100_R01List(valueIndex, updateData as SYS4100_R01Dto);
				handlePopupClose();
			};

			const wrapperOptionMap: Record<string, { size: string; width: number; overlay: boolean }> = {
				orgAdmin: { size: "md", width: 50, overlay: true },
				upOrgCd: { size: "sm", width: 30, overlay: true },
				mergeOrgCd: { size: "sm", width: 30, overlay: true },
			};

			setCodeSearchModalProps({
				category,
				search,
				formName: "SYS4100_R01",
				isInfo,
				handleFoucsId: handleFocusId,
				onClear: handleClear,
				onConfirm: (data: any) => {
					const currentRow =
						SYS4100_R01List.find((item) => item.valueIndex === valueIndex) ??
						originalSYS4100_R01List.find((item) => item.valueIndex === valueIndex) ??
						originalData;

					const updateData: Partial<SYS4100_R01Dto> = {
						...currentRow,
						isModified: originalData.inserted ? false : true,
					};

					if (handleFocusId === "upOrgCd") {
						updateData.upOrgCd = data.orgCd ?? "";
						updateData.upOrgNm = data.orgNm ?? "";
					} else if (handleFocusId === "orgAdmin") {
						updateData.orgAdmin = data.empNo ?? "";
						updateData.orgAdminNm = data.empNm ?? "";
					} else if (handleFocusId === "mergeOrgCd") {
						updateData.mergeOrgCd = data.orgCd ?? "";
						updateData.mergeOrgNm = data.orgNm ?? "";
					}

					updateSYS4100_R01List(valueIndex, updateData as SYS4100_R01Dto);

					setTimeout(() => {
						if (!gridRef.current) return;
						if (handleFocusId === "upOrgCd") {
							focusToCell(gridRef, valueIndex, "displaySeq");
						} else if (handleFocusId === "orgAdmin") {
							focusToCell(gridRef, valueIndex, "dueDt");
						} else if (handleFocusId === "mergeOrgCd") {
							focusToCell(gridRef, valueIndex, "dueDt");
						}
					}, 100);
				},
				wrapperOption: wrapperOptionMap[handleFocusId] ?? { size: "md", width: 50, overlay: true },
			});
		}, [setCodeSearchModalProps, updateSYS4100_R01List, SYS4100_R01List, originalSYS4100_R01List, gridRef, handlePopupClose]);

	return { settingCodeSearchModalProps, handlePopupClose };
}
